<template>
  <div class="p-6 bg-white border-t border-gray-200">
    <ValidationObserver ref="observer">
      <template #default="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(onSubmit)" class="grid gap-4">
          <div class="grid grid-flow-col gap-2 justify-start">
            <BaseButton primary tag="label" for="file"> Select Files </BaseButton>
            <input
              id="file"
              type="file"
              ref="file"
              multiple
              class="hidden"
              :accept="accept.join(',')"
              @change="onChangeFile($event.target.files)"
            />

            <BaseButton type="button" ghost :disabled="!hasMedia" @click="media = {}">
              Clear Files
            </BaseButton>
          </div>

          <ul class="grid gap-2">
            <template v-if="hasMedia">
              <li v-for="medium in media" :key="medium.id" class="border rounded px-4 py-3">
                <div class="flex -mx-4">
                  <div class="flex-1 px-4">
                    {{ medium.file.name }}
                  </div>
                  <div v-if="medium.pending" class="flex-none px-4">
                    Uploading {{ medium.progress }}%
                  </div>
                </div>
              </li>
            </template>
            <li v-else class="border rounded px-4 py-3">No files selected</li>
          </ul>

          <ul v-if="hasResolvedMedia" class="grid gap-2">
            <li v-for="medium in resolvedMedia" :key="medium.id" class="border rounded px-4 py-3">
              <div class="flex -mx-4">
                <div class="flex-1 px-4">
                  {{ medium.file.name }}
                </div>
                <div class="flex-none px-4 text-green-600">Completed</div>
              </div>
            </li>
          </ul>

          <ul v-if="hasRejectedMedia" class="grid gap-2">
            <li v-for="medium in rejectedMedia" :key="medium.id" class="border rounded px-4 py-3">
              <div class="flex -mx-4">
                <div class="flex-1 px-4">
                  {{ medium.file.name }}
                </div>
                <div class="flex-none px-4 text-red-600">Failed</div>
              </div>
            </li>
          </ul>

          <div class="grid grid-flow-col gap-2 justify-start">
            <BaseButton success :disabled="!hasMedia"> Upload Files </BaseButton>

            <BaseButton type="button" ghost @click="cancel"> Cancel </BaseButton>
          </div>
        </form>
      </template>
    </ValidationObserver>
  </div>
</template>

<script>
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';

function generateBlobFromFile(file) {
  if (file.type.substr(0, 6) !== 'image/') {
    return '';
  }

  const URL = window.URL || window.webkitURL;

  if (!URL) {
    return '';
  }

  if (!URL.createObjectURL) {
    return '';
  }

  return URL.createObjectURL(file);
}

export default {
  mixins: [veeValidateErrorsMixin],

  props: {
    orderItemId: {
      type: String,
      required: true,
    },
    accept: {
      type: Array,
      default: () => ['image/x-png', 'image/gif', 'image/jpeg', 'application/pdf'],
    },
  },

  data() {
    return {
      creating: false,
      media: {},
      resolvedMedia: {},
      rejectedMedia: {},
      errorMsg: '',
    };
  },

  computed: {
    hasMedia() {
      return Object.keys(this.media).length > 0;
    },
    hasResolvedMedia() {
      return Object.keys(this.resolvedMedia).length > 0;
    },
    hasRejectedMedia() {
      return Object.keys(this.rejectedMedia).length > 0;
    },
  },

  methods: {
    onChangeFile(fileList) {
      Array.from(fileList).forEach((file) => {
        if (!this.accept.indexOf(file.type) === -1) {
          return;
        }

        const id = Math.random().toString(36).substr(2);
        const blob = generateBlobFromFile(file);

        this.media = {
          ...this.media,
          [id]: {
            id,
            file,
            blob,
            thumb: blob,
            data: {
              order_item: this.orderItemId,
            },
            progress: 0,
            pending: false,
            resolved: false,
            rejected: false,
          },
        };
      });

      this.$refs.file.value = '';
    },

    onSubmit() {
      Object.keys(this.media).forEach((key) => {
        const media = this.media[key];

        const config = {
          onUploadProgress(progressEvent) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            media.progress = progress;
          },
        };

        const onResolved = () => {
          media.pending = false;
          media.resolved = true;
          const { [media.id]: resolvedMedia, ...restMedia } = this.media;
          this.resolvedMedia = { ...this.resolvedMedia, [media.id]: resolvedMedia };
          this.media = { ...restMedia };
        };

        const onRejected = () => {
          media.pending = false;
          media.rejected = true;
          const { [media.id]: rejectedMedia, ...restMedia } = this.media;
          this.rejectedMedia = { ...this.rejectedMedia, [media.id]: rejectedMedia };
          this.media = { ...restMedia };
        };

        media.pending = true;

        const body = new FormData();
        body.append('file', media.file);

        Object.keys(media.data).forEach((dataKey) => {
          const value = media.data[dataKey];

          if (typeof value === 'object' && value !== null) {
            body.append(dataKey, JSON.stringify(value));
            return;
          }

          body.append(dataKey, value);
        });

        this.uploadMedia(body, config, onResolved, onRejected);
      });
    },

    setCreating(value) {
      this.creating = value;
      this.$emit('creating', value);
    },
    async uploadMedia(body, config, resolved, rejected) {
      try {
        const result = await this.$store.dispatch({
          type: 'ui/media/upload',
          body,
          config,
        });

        this.$emit('created', result);
      } catch (error) {
        rejected(error);
      } finally {
        //
      }
    },
    cancel() {
      this.$emit('cancel');
    },
    applyServerErrors(errors) {
      this.observer = this.$refs.observer;
      this.addServerErrors(errors);
    },
  },
};
</script>
